import { FormOverride } from "../../types";
import {
  GENERAL_FORM_CONSTANTS,
  INCIDENT_INFORMATION_CONSTANTS,
  PHYSICIAN_INFORMATION_CONSTANTS,
  FILE_ATTACHMENTS_CONSTANTS,
} from "../../constants";
import {
  ANCHOR_ACCIDENT_OPTIONS,
  ANCHOR_BODY_PART_OPTIONS,
  ANCHOR_CAUSES_OPTIONS,
  ANCHOR_INJURY_OPTIONS,
  CONCERNS_OPTIONS,
  USA_STATES,
} from "src/forms/options";

export const anchorFormOverride: FormOverride = {
  state: {
    [INCIDENT_INFORMATION_CONSTANTS.NATURE_OF_INJURY]: {
      hidden: false,
      ignoreInFinalForm: false,
    },
    [INCIDENT_INFORMATION_CONSTANTS.BODY_PART_INFORMATION_0]: {
      hidden: false,
      ignoreInFinalForm: false,
    },
    [INCIDENT_INFORMATION_CONSTANTS.BODY_PART_INFORMATION_1]: {
      hidden: false,
      ignoreInFinalForm: false,
    },
    [INCIDENT_INFORMATION_CONSTANTS.PRIMARY_INJURED_BODY_PART]: {
      hidden: false,
      ignoreInFinalForm: false,
    },
    [INCIDENT_INFORMATION_CONSTANTS.SECONDARY_INJURED_BODY_PART]: {
      hidden: false,
      ignoreInFinalForm: false,
    },
    [INCIDENT_INFORMATION_CONSTANTS.PRIMARY_INJURED_SIDE]: {
      hidden: true,
      ignoreInFinalForm: true,
    },
    [INCIDENT_INFORMATION_CONSTANTS.SECONDARY_INJURED_SIDE]: {
      hidden: true,
      ignoreInFinalForm: true,
    },
    [INCIDENT_INFORMATION_CONSTANTS.ADD_BODY_PART_NAME]: {
      hidden: true,
      ignoreInFinalForm: true,
    },
    [INCIDENT_INFORMATION_CONSTANTS.SOURCE_OF_INJURY]: {
      hidden: true,
      ignoreInFinalForm: true,
    },
    [PHYSICIAN_INFORMATION_CONSTANTS.INITIAL_MEDICAL_TREATMENT]: {
      hidden: true,
      ignoreInFinalForm: true,
    },
    [INCIDENT_INFORMATION_CONSTANTS.SECONDARY_NATURE_OF_INJURY]: {
      hidden: true,
      ignoreInFinalForm: true,
    },
    [INCIDENT_INFORMATION_CONSTANTS.ACCIDENT_TYPE]: {
      hidden: false,
      ignoreInFinalForm: false,
    },
    [PHYSICIAN_INFORMATION_CONSTANTS.EMS_CALLED]: {
      hidden: true,
      ignoreInFinalForm: true,
    },
    [PHYSICIAN_INFORMATION_CONSTANTS.EMS_ASSESSMENT]: {
      hidden: true,
      ignoreInFinalForm: true,
    },
    [INCIDENT_INFORMATION_CONSTANTS.REFERRAL_REASONS]: {
      hidden: true,
      ignoreInFinalForm: true,
    },
    [FILE_ATTACHMENTS_CONSTANTS.AVAILABLE_MEDICAL_DOCUMENTS_ATTACHMENTS]: {
      hidden: true,
      ignoreInFinalForm: true,
    },
    [FILE_ATTACHMENTS_CONSTANTS.IRF_OVERRIDE]: {
      hidden: false,
    },
  },
  config: {
    [INCIDENT_INFORMATION_CONSTANTS.NATURE_OF_INJURY]: {
      label: "Nature of Injury",
      options: ANCHOR_INJURY_OPTIONS,
    },
    [INCIDENT_INFORMATION_CONSTANTS.ACCIDENT_TYPE]: {
      label: "Accident Type",
      placeholderText: "Please select the type of accident that occurred",
      options: ANCHOR_ACCIDENT_OPTIONS,
    },
    [INCIDENT_INFORMATION_CONSTANTS.CAUSE_OF_INJURY]: {
      label: "Cause of Injury",
      options: ANCHOR_CAUSES_OPTIONS,
    },
    [INCIDENT_INFORMATION_CONSTANTS.PRIMARY_INJURED_BODY_PART]: {
      options: ANCHOR_BODY_PART_OPTIONS,
    },
    [INCIDENT_INFORMATION_CONSTANTS.SECONDARY_INJURED_BODY_PART]: {
      options: ANCHOR_BODY_PART_OPTIONS,
    },
    [INCIDENT_INFORMATION_CONSTANTS.QUESTIONABLE_CLAIM_REASONS]: {
      options: CONCERNS_OPTIONS,
    },
    [INCIDENT_INFORMATION_CONSTANTS.BODY_PART_INFORMATION_0]: {
      contained: false,
    },
    [INCIDENT_INFORMATION_CONSTANTS.BODY_PART_INFORMATION_1]: {
      contained: false,
    },
    [PHYSICIAN_INFORMATION_CONSTANTS.HOSPITAL_INFORMATION_STATE]: {
      options: USA_STATES,
    },
    [PHYSICIAN_INFORMATION_CONSTANTS.PHYSICIAN_INFORMATION_0_STATE]: {
      options: USA_STATES,
    },
    [PHYSICIAN_INFORMATION_CONSTANTS.PHYSICIAN_INFORMATION_1_STATE]: {
      options: USA_STATES,
    },
  },
  validation: {
    [INCIDENT_INFORMATION_CONSTANTS.NATURE_OF_INJURY]: {
      required: { value: true },
    },
    [INCIDENT_INFORMATION_CONSTANTS.ACCIDENT_TYPE]: {
      required: { value: true },
    },
    [INCIDENT_INFORMATION_CONSTANTS.PRIMARY_INJURED_BODY_PART]: {
      required: { value: true },
    },
    [INCIDENT_INFORMATION_CONSTANTS.PRIMARY_INJURED_SIDE]: {
      required: { value: false },
    },
    [INCIDENT_INFORMATION_CONSTANTS.SOURCE_OF_INJURY]: {
      required: { value: false },
    },
    [PHYSICIAN_INFORMATION_CONSTANTS.INITIAL_MEDICAL_TREATMENT]: {
      required: { value: false },
    },
    [INCIDENT_INFORMATION_CONSTANTS.SECONDARY_INJURED_BODY_PART]: {
      required: { value: false },
    },
    [INCIDENT_INFORMATION_CONSTANTS.SECONDARY_NATURE_OF_INJURY]: {
      required: { value: false },
    },
    [FILE_ATTACHMENTS_CONSTANTS.INITIAL_REPORT_FORM]: {
      required: {
        value: true,
        message: FILE_ATTACHMENTS_CONSTANTS.IRF_REQUIRED_ERROR,
      },
    },
    [FILE_ATTACHMENTS_CONSTANTS.AUTHORIZATION_FOR_INITIAL_MEDICAL_EVALUATION]: {
      required: {
        value: true,
        message:
          FILE_ATTACHMENTS_CONSTANTS.AUTHORIZATION_FOR_INITIAL_MEDICAL_EVALUATION_REQUIRED_ERROR,
      },
    },
    [FILE_ATTACHMENTS_CONSTANTS.AUSTIN_NOTES]: {
      required: {
        value: true,
        message: FILE_ATTACHMENTS_CONSTANTS.AUSTIN_NOTES_REQUIRED_ERROR,
      },
    },
    // Reset postal code validations to US defaults (overrides Canada-specific settings)
    [PHYSICIAN_INFORMATION_CONSTANTS.PHYSICIAN_INFORMATION_0_ZIPCODE]: {
      allowedCharactersRegExp: {
        value: new RegExp(GENERAL_FORM_CONSTANTS.ONLY_NUMBERS_REGEX),
        message: GENERAL_FORM_CONSTANTS.ZIP_CODE_INVALID_CHARACTERS_MESSAGE,
      },
      maxCharacters: { value: 5 },
    },
    [PHYSICIAN_INFORMATION_CONSTANTS.PHYSICIAN_INFORMATION_1_ZIPCODE]: {
      allowedCharactersRegExp: {
        value: new RegExp(GENERAL_FORM_CONSTANTS.ONLY_NUMBERS_REGEX),
        message: GENERAL_FORM_CONSTANTS.ZIP_CODE_INVALID_CHARACTERS_MESSAGE,
      },
      maxCharacters: { value: 5 },
    },
    [PHYSICIAN_INFORMATION_CONSTANTS.HOSPITAL_INFORMATION_ZIPCODE]: {
      allowedCharactersRegExp: {
        value: new RegExp(GENERAL_FORM_CONSTANTS.ONLY_NUMBERS_REGEX),
        message: GENERAL_FORM_CONSTANTS.ZIP_CODE_INVALID_CHARACTERS_MESSAGE,
      },
      maxCharacters: { value: 5 },
    },
  },
};
